import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {VisualHistoryEventListItemFragment} from 'molbio/visual-history/__generated/VisualHistoryEventListItemFragment';
import {FONT_PROXIMA_12, FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_DARKER} from 'ui-library/Colors';
import Link from 'ui-library/Link';
import {readableTime} from 'util/DateUtil';

type ActorNode = VisualHistoryEventListItemFragment['user'];

export interface VisualHistoryEventHeaderProps {
  headerInfo: {
    user: ActorNode | null;
    message: string;
    createdAt: string;
  };
  onClickCreatedAt: (() => void) | null;
}

/**
 * Renders the header for a visual history event consisting of a message, a clickable version link and a user.
 * @param headerInfo Header information (user, message, created at timestamp).
 * @param onClickCreatedAt Callback for clicking on the created at link.
 * @constructor
 */
export default function VisualHistoryEventHeader({
  headerInfo,
  onClickCreatedAt,
}: VisualHistoryEventHeaderProps): JSX.Element {
  return (
    <div {...getTestProps({component: 'VisualHistoryEventHeader', element: 'root'})}>
      <p
        className={css(styles.message)}
        {...getTestProps({component: 'VisualHistory', element: 'eventName'})}
      >
        {headerInfo.message}
      </p>
      <div>
        {onClickCreatedAt ? (
          <Link
            dataTestAttrs={{component: 'VisualHistoryEventHeader', element: 'createdAt'}}
            onClick={onClickCreatedAt}
          >
            {readableTime(headerInfo.createdAt)}
          </Link>
        ) : (
          <span {...getTestProps({component: 'VisualHistoryEventHeader', element: 'createdAt'})}>
            {readableTime(headerInfo.createdAt)}
          </span>
        )}
        {headerInfo.user && (
          <span
            className={css(styles.user)}
            {...getTestProps({component: 'VisualHistoryEventHeader', element: 'user'})}
          >
            {` by ${isInaccessible(headerInfo.user) ? 'Inaccessible' : headerInfo.user.name}`}
          </span>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  message: {
    ...FONT_PROXIMA_TITLE,
    marginBottom: '8px',
  },
  user: {
    ...FONT_PROXIMA_12,
    color: GRAY_DARKER,
  },
});
